/**
 * An expert's personal rubric templates: named sets of criteria the expert adds to their own
 * rubrics. The server keys every template by the signed-in expert, so these calls only ever
 * reach the caller's own templates.
 */
import { api } from '@/api/client'
import schema from '@schemas/rubric_template.schema.json'
import rubricUpdate from '@schemas/rubric_update.schema.json'
import { validate } from '@/lib/schema'
import { isBlank } from '@/lib/rubricTemplateInsert'
import type { RubricCriterion, RubricTemplate, RubricTemplateSummary, TemplateCriterion } from '@/lib/types'

/** The management page; the API uses the same path. */
export const TEMPLATES_ROUTE = '/sme/rubric-templates'
const API = '/sme/rubric-templates'
export const MAX_TEMPLATE_NAME = schema.properties.name.maxLength
const MIN_CRITERIA = schema.properties.criteria.minItems
const MAX_CRITERIA = schema.properties.criteria.maxItems
/** A rubric holds at most this many criteria, so a template is only added while it fits. */
export const MAX_RUBRIC_CRITERIA = rubricUpdate.properties.rubric.maxItems

/** The expert's templates, and how many the server lets them keep. */
export const listTemplates = () => api.get<{ templates: RubricTemplateSummary[]; limit: number }>(API)
export const getTemplate = (id: string) => api.get<RubricTemplate>(`${API}/${encodeURIComponent(id)}`)
export const deleteTemplate = (id: string) => api.del(`${API}/${encodeURIComponent(id)}`)

/**
 * Creates a template, or replaces `target`. Replacing sends the `updated_at` the expert saw; the
 * server answers 412 when the template was saved elsewhere since, and 409 for a name another of
 * the expert's templates has.
 */
export function saveTemplate(name: string, criteria: TemplateCriterion[], target?: Pick<RubricTemplateSummary, 'template_id' | 'updated_at'>) {
  const body = { name: name.trim().normalize('NFC'), criteria }
  return target
    ? api.put<RubricTemplate>(`${API}/${encodeURIComponent(target.template_id)}`, { ...body, updated_at: target.updated_at })
    : api.post<RubricTemplate>(API, body)
}

/** Whether two template names are the same one to the server (NFC, then Python's casefold, approximated). */
export const sameName = (a: string, b: string) => {
  const fold = (s: string) => s.trim().normalize('NFC').toUpperCase().toLowerCase()
  return fold(a) === fold(b)
}


/** The criteria a rubric contributes to a template: its non-blank cards, without ids or engineer-only fields. */
export function templateCriteria(rubric: RubricCriterion[]): TemplateCriterion[] {
  return rubric.filter((c) => !isBlank(c)).map((c) => ({
    role: c.role, criterion: c.criterion.trim(), ...(c.evidence?.trim() ? { evidence: c.evidence.trim() } : {}),
  }))
}

/** Why a template cannot have this name, in plain words, or '' when it can (the server also normalises it to NFC). */
export function nameProblem(name: string): string {
  const stored = name.trim().normalize('NFC')
  if (!stored) return 'Give the template a name.'
  if (stored.length > MAX_TEMPLATE_NAME) return `Keep the name to ${MAX_TEMPLATE_NAME} characters.`
  return validate('rubric_template', { name: stored, criteria: [] }).some((e) => e.path === 'name')
    ? 'Keep the name to one line, without tabs.' : ''
}

/** Why this rubric's cards cannot be saved as a template, naming the card by its ID (R3), or '' when they can. */
export function templateProblem(rubric: RubricCriterion[]): string {
  const cards = rubric.filter((c) => !isBlank(c))
  if (cards.length < MIN_CRITERIA) return `A template needs at least ${MIN_CRITERIA} written criteria.`
  if (cards.length > MAX_CRITERIA) return `A template holds up to ${MAX_CRITERIA} criteria; this one has ${cards.length}.`
  const unwritten = cards.find((c) => !c.criterion.trim())
  if (unwritten) return `${unwritten.criterion_id} has “How would you check it?” but no criterion. Write the criterion, or clear the card.`
  const fields: Record<string, string> = { evidence: '“How would you check it?”' }
  // One message per field: a criterion can break several rules at once.
  const errors = validate('rubric_template', { name: 'name', criteria: templateCriteria(rubric) })
    .filter((e, i, all) => e.path.startsWith('criteria.') && all.findIndex((other) => other.path === e.path) === i)
  return errors.map((e) => {
    const [, index, field] = /^criteria\.(\d+)\.?(\w*)/.exec(e.path) ?? []
    return index === undefined ? e.message : `${cards[Number(index)].criterion_id}, ${fields[field] ?? 'criterion'}: ${e.message}`
  }).join(' ')
}

export const countLabel = (n: number) => `${n} ${n === 1 ? 'criterion' : 'criteria'}`
